import { useEffect, useRef, useState, useCallback } from 'react';
import { ArrowDown } from 'lucide-react';
import clsx from 'clsx';
import type { ChatMessage } from '../../types/chat';
import { MessageBubble } from './MessageBubble';
import { SuggestionChips } from './SuggestionChips';
import { useChatStore } from '../../store/chatStore';

function groupMessages(messages: ChatMessage[]): (ChatMessage | { type: 'tool_summary'; tools: string[]; id: string })[] {
  const result: (ChatMessage | { type: 'tool_summary'; tools: string[]; id: string })[] = [];
  let i = 0;
  while (i < messages.length) {
    const msg = messages[i];
    if (msg.type === 'tool_call' && msg.toolCall?.status === 'completed') {
      const tools: string[] = [msg.toolCall.tool];
      while (
        i + 1 < messages.length &&
        messages[i + 1].type === 'tool_call' &&
        messages[i + 1].toolCall?.status === 'completed'
      ) {
        i++;
        tools.push(messages[i].toolCall!.tool);
      }
      result.push({ type: 'tool_summary', tools, id: msg.id });
    } else {
      result.push(msg);
    }
    i++;
  }
  return result;
}

interface MessageListProps {
  messages: ChatMessage[];
  onConfirm: (actionId: string) => void;
  onCancel: (actionId: string) => void;
  confirmLoadingId?: string | null;
  resolvedIds: Set<string>;
  onSendSuggestion: (text: string) => void;
}

export const MessageList = ({
  messages,
  onConfirm,
  onCancel,
  confirmLoadingId,
  resolvedIds,
  onSendSuggestion,
}: MessageListProps) => {
  const { streamingMessageId, isStreaming: storeIsStreaming } = useChatStore();

  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Auto-scroll on new messages
  useEffect(() => {
    if (!showScrollButton) {
      scrollToBottom();
    }
  }, [messages, showScrollButton, scrollToBottom]);

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;

    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    setShowScrollButton(!isNearBottom);
  };

  return (
    <div className="relative flex-1 overflow-hidden">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-full overflow-y-auto px-3 py-3 space-y-3"
      >
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <p className="text-gray-400 dark:text-gray-500 text-sm">
              Ask me about invoices, approvals, or vendors
            </p>
            <SuggestionChips onSelect={onSendSuggestion} />
          </div>
        )}

        {groupMessages(messages).map((item) => {
          if ('tools' in item && item.type === 'tool_summary') {
            return (
              <div key={item.id} className="flex items-center gap-1.5 px-3 py-1 text-xs text-gray-400 dark:text-gray-500">
                <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                <span>Used {item.tools.length} tool{item.tools.length > 1 ? 's' : ''}</span>
              </div>
            );
          }
          const msg = item as ChatMessage;
          return (
            <MessageBubble
              key={msg.id}
              message={msg}
              onConfirm={onConfirm}
              onCancel={onCancel}
              isConfirmLoading={confirmLoadingId === msg.confirmation?.action_id}
              isConfirmResolved={msg.confirmation ? resolvedIds.has(msg.confirmation.action_id) : false}
              isStreaming={msg.id === streamingMessageId}
            />
          );
        })}

        {storeIsStreaming && !streamingMessageId && (
          <div className="flex items-center gap-1.5 px-3 py-2 text-gray-400">
            <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.3s]" />
            <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.15s]" />
            <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce" />
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {showScrollButton && (
        <button
          onClick={scrollToBottom}
          className={clsx(
            'absolute bottom-2 left-1/2 -translate-x-1/2',
            'flex items-center gap-1 px-3 py-1 rounded-full text-xs',
            'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800',
            'shadow-lg hover:opacity-90 transition-opacity'
          )}
        >
          <ArrowDown className="h-3 w-3" />
          New messages
        </button>
      )}
    </div>
  );
};
